import { useEffect, useState } from "react";
import { SortOrder } from "../api/common/models";
import { defaultPageIndex, maxPageSize } from "../api/data-sources";
import { DataSourceOrderBy } from "../api/models/DataSource";
import SingleSelectDropdown from "./SingleSelectFilterToolbarDropdown";

type SortByOption =
    | "nameAsc"
    | "nameDesc"
    | "uniqueReferenceAsc"
    | "uniqueReferenceDesc"
    | "locationNameAsc"
    | "locationNameDesc"
    | "scopeAsc"
    | "scopeDesc";

const sortByOptions: { label: string; value: SortByOption }[] = [
    {
        label: "Name Asc",
        value: "nameAsc",
    },
    {
        label: "Name Desc",
        value: "nameDesc",
    },
    {
        label: "Unique Reference Asc",
        value: "uniqueReferenceAsc",
    },
    {
        label: "Unique Reference Desc",
        value: "uniqueReferenceDesc",
    },
    {
        label: "Location Asc",
        value: "locationNameAsc",
    },
    {
        label: "Location Desc",
        value: "locationNameDesc",
    },
    {
        label: "Scope Asc",
        value: "scopeAsc",
    },
    {
        label: "Scope Desc",
        value: "scopeDesc",
    },
];

const sortyByOptionDetailMap: Record<
    SortByOption,
    {
        sortOrder: SortOrder;
        orderBy: DataSourceOrderBy;
    }
> = {
    nameAsc: {
        sortOrder: "asc",
        orderBy: "Name",
    },
    nameDesc: {
        sortOrder: "desc",
        orderBy: "Name",
    },
    uniqueReferenceAsc: {
        sortOrder: "asc",
        orderBy: "UniqueReference",
    },
    uniqueReferenceDesc: {
        sortOrder: "desc",
        orderBy: "UniqueReference",
    },
    locationNameAsc: {
        sortOrder: "asc",
        orderBy: "LocationName",
    },
    locationNameDesc: {
        sortOrder: "desc",
        orderBy: "LocationName",
    },
    scopeAsc: {
        sortOrder: "asc",
        orderBy: "Scope",
    },
    scopeDesc: {
        sortOrder: "desc",
        orderBy: "Scope",
    },
};

const getInitialValue = (
    orderBy: DataSourceOrderBy,
    sortOrder: SortOrder
): SortByOption => {
    let initialValue = "nameAsc";
    for (const [key, value] of Object.entries(sortyByOptionDetailMap)) {
        if (value.orderBy === orderBy && value.sortOrder === sortOrder) {
            initialValue = key;
        }
    }

    return initialValue as SortByOption;
};

type DataSourceSortByDropdownProps = {
    orderBy: DataSourceOrderBy;
    sortOrder: SortOrder;
    setOrderBy: (value: DataSourceOrderBy) => void;
    setSortOrder: (value: SortOrder) => void;
    setPageSize: (value: number) => void;
    setPageNumber: (value: number) => void;
};

const DataSourceSortByDropdown = ({
    orderBy,
    sortOrder,
    setOrderBy,
    setSortOrder,
    setPageSize,
    setPageNumber,
}: DataSourceSortByDropdownProps) => {
    const [sortBy, setSortBy] = useState<SortByOption>(
        getInitialValue(orderBy, sortOrder)
    );

    useEffect(() => {
        const sortByDetail = sortyByOptionDetailMap[sortBy];

        setOrderBy(sortByDetail.orderBy);
        setSortOrder(sortByDetail.sortOrder);
        setPageSize(maxPageSize);
        setPageNumber(defaultPageIndex);
    }, [sortBy, setOrderBy, setSortOrder, setPageNumber, setPageSize]);

    return (
        <SingleSelectDropdown
            label="Sort by"
            selected={sortBy}
            options={sortByOptions}
            name="sortBy"
            onSelect={setSortBy}
            onReset={() => setSortBy("nameAsc")}
        />
    );
};

export default DataSourceSortByDropdown;
